export type ReportTypeKey =
  | "trade-analysis"
  | "market-context-performance"
  | "trade-management"
  | string;

/** User-facing report type names (API keys unchanged). */
export const REPORT_TYPE_DISPLAY_LABELS: Record<string, string> = {
  "trade-analysis": "Performance Review",
  "market-context-performance": "Entry Analysis",
  "trade-management": "Exit Placement",
};

/** User-facing subtitle for Exit Placement (API type remains trade-management). */
export const EXIT_PLACEMENT_SUBTITLE =
  "Recommended stop and target distances when your market context matches";

/** Simulator / product copy vs Entry Analysis (“filter which trades to take”). */
export const EXIT_PLACEMENT_SIM_PRODUCT_COPY =
  "Change stop and target distances when this context matches";

export function normalizeReportTypeKey(reportType?: string): string {
  return String(reportType ?? "trade-analysis")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/\s+/g, "-");
}

export function isTradeAnalysisReportType(reportType?: string): boolean {
  return normalizeReportTypeKey(reportType) === "trade-analysis";
}

export function isTradeManagementReportType(reportType?: string): boolean {
  return normalizeReportTypeKey(reportType) === "trade-management";
}

export function isMarketContextReportType(reportType?: string): boolean {
  const key = normalizeReportTypeKey(reportType);
  return key === "market-context-performance" || key === "trade-management";
}

/** True for Entry Analysis reports (`market-context-performance`). */
export function isEntryAnalysisReportType(reportType?: string): boolean {
  return normalizeReportTypeKey(reportType) === "market-context-performance";
}

/** @deprecated Prefer {@link isEntryAnalysisReportType}. */
export const isRegimeAnalysisReportType = isEntryAnalysisReportType;

export type ReportDetailSectionKey =
  | "overview"
  | "market"
  | "simulator"
  | "pine"
  | "notes";

const ENTRY_ANALYSIS_DETAIL_SECTIONS: ReportDetailSectionKey[] = [
  "overview",
  "market",
  "simulator",
  "notes",
];

/** Exit Placement — same body sections as Entry Analysis, without Pinescript. */
const EXIT_PLACEMENT_DETAIL_SECTIONS: ReportDetailSectionKey[] = [
  "overview",
  "market",
  "simulator",
  "notes",
];

const FULL_DETAIL_SECTIONS: ReportDetailSectionKey[] = [
  "overview",
  "market",
  "simulator",
  "pine",
  "notes",
];

export function reportDetailSectionKeys(reportType: string | undefined): readonly ReportDetailSectionKey[] {
  if (isTradeAnalysisReportType(reportType)) {
    return ["overview"] as const;
  }
  if (isEntryAnalysisReportType(reportType)) {
    return ENTRY_ANALYSIS_DETAIL_SECTIONS;
  }
  if (isTradeManagementReportType(reportType)) {
    return EXIT_PLACEMENT_DETAIL_SECTIONS;
  }
  return FULL_DETAIL_SECTIONS;
}

export function getReportTypeDisplayLabel(reportTypeKey?: string | null): string {
  const key = normalizeReportTypeKey(reportTypeKey || "trade-analysis");
  const mapped = REPORT_TYPE_DISPLAY_LABELS[key];
  if (mapped) return mapped;
  const raw = String(reportTypeKey ?? "").trim();
  if (!raw) return REPORT_TYPE_DISPLAY_LABELS["trade-analysis"];
  return raw
    .split(/[-_]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Prefer BE-provided `reportTypeLabel` when present; remap known legacy product
 * names (Regime Analysis → Entry Analysis). Fall back to the key map.
 */
export function resolveReportTypeDisplayLabel(
  reportTypeKey?: string | null,
  reportTypeLabel?: string | null
): string {
  const fromLabel = String(reportTypeLabel ?? "").trim();
  if (fromLabel) {
    return LEGACY_REPORT_TYPE_DISPLAY_LABELS[fromLabel] ?? fromLabel;
  }
  return getReportTypeDisplayLabel(reportTypeKey);
}

/** Older product names still present on cached workspace/report meta. */
const LEGACY_REPORT_TYPE_DISPLAY_LABELS: Record<string, string> = {
  "Regime Analysis": "Entry Analysis",
  "Market Context Performance": "Entry Analysis",
};

export type ReportTypeOptionLike = {
  key: string;
  label: string;
  description?: string;
};

/** Prefer labels from GET /api/reports/types; fall back to local map for missing labels. */
export function mapReportTypeOptionsForDisplay<T extends ReportTypeOptionLike>(options: T[]): T[] {
  return options.map((opt) => {
    const apiLabel = String(opt.label ?? "").trim();
    if (apiLabel) {
      return { ...opt, label: apiLabel };
    }
    return { ...opt, label: getReportTypeDisplayLabel(opt.key) };
  });
}

/** Backend default when `include_market_context` is omitted. */
export function defaultIncludeMarketContext(reportType?: string): boolean {
  return !isTradeAnalysisReportType(reportType);
}

/** Symbol required when market context is enabled (explicit or type default). */
export function isSymbolRequiredForGenerate(
  reportType: string,
  includeMarketContext: boolean
): boolean {
  return includeMarketContext;
}

/** Saved reports table: Performance Review without a focus symbol is account-wide. */
export function getReportSymbolTableLabel(
  reportType: string | undefined,
  symbol: string | null | undefined
): string {
  const trimmed = String(symbol ?? "").trim();
  if (trimmed) return trimmed;
  if (isTradeAnalysisReportType(reportType)) return "N/A";
  return "—";
}

/** Whether the generate wizard should collect symbol + candle settings. */
export function showMarketContextFieldsOnGenerate(reportType: string): boolean {
  return !isTradeAnalysisReportType(reportType);
}

/** Performance Review: optional multi-symbol scope on generate (no market context). */
export function showPerformanceReviewSymbolFieldsOnGenerate(reportType: string): boolean {
  return isTradeAnalysisReportType(reportType);
}

export type ReportSymbolScopeMode = "none" | "market_context" | "performance_review";

/** Which symbol UI the generate wizard should show for a report type. */
export function reportSymbolScopeMode(reportType?: string): ReportSymbolScopeMode {
  if (isTradeAnalysisReportType(reportType)) return "performance_review";
  if (reportType?.trim() && showMarketContextFieldsOnGenerate(reportType)) return "market_context";
  return "none";
}

export function normalizePerformanceReviewSymbol(raw: string): string {
  return String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/\//g, "");
}

/** Map wizard state to POST /reports/generate symbol fields. */
export function buildPerformanceReviewSymbolsPayload(input: {
  allSymbols: boolean;
  selectedSymbols: string[];
}): { symbol?: string; symbols?: string[] } {
  if (input.allSymbols) return {};
  const symbols: string[] = [];
  const seen = new Set<string>();
  for (const raw of input.selectedSymbols) {
    const normalized = normalizePerformanceReviewSymbol(raw);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    symbols.push(normalized);
  }
  if (symbols.length === 0) return {};
  if (symbols.length === 1) return { symbol: symbols[0] };
  return { symbols };
}

export function effectiveIncludeMarketContextForGenerate(reportType: string): boolean {
  return defaultIncludeMarketContext(reportType);
}

export const TRADE_ANALYSIS_DETAIL_TAB_LABELS = ["Insights"] as const;
export const TRADE_ANALYSIS_DETAIL_TAB_TITLES = ["TES insights & findings"] as const;

export function reportTypeRequiresInstrumentSetup(reportType?: string): boolean {
  const key = normalizeReportTypeKey(reportType);
  return key === "trade-management" || key === "market-context-performance";
}

export function reportDetailSectionAtTab(
  reportType: string | undefined,
  tabIndex: number
): ReportDetailSectionKey {
  const keys = reportDetailSectionKeys(reportType);
  return keys[tabIndex] ?? "overview";
}

export function reportDetailTabCount(reportType: string | undefined): number {
  return reportDetailSectionKeys(reportType).length;
}

const ENTRY_ANALYSIS_DETAIL_TAB_LABELS = ["Overview", "Market", "Simulator", "Notes"] as const;
const ENTRY_ANALYSIS_DETAIL_TAB_TITLES = [
  "Overview",
  "Entry Analysis",
  "Simulator",
  "Notes",
] as const;

const EXIT_PLACEMENT_DETAIL_TAB_LABELS = [
  "Overview",
  "Exit Placement Explorer",
  "Simulator",
  "Notes",
] as const;
const EXIT_PLACEMENT_DETAIL_TAB_TITLES = [
  "Overview",
  "Exit Placement Explorer",
  "Simulator",
  "Notes",
] as const;

export function reportDetailTabLabels(reportType: string | undefined): readonly string[] {
  if (isTradeAnalysisReportType(reportType)) {
    return TRADE_ANALYSIS_DETAIL_TAB_LABELS;
  }
  if (isEntryAnalysisReportType(reportType)) {
    return ENTRY_ANALYSIS_DETAIL_TAB_LABELS;
  }
  if (isTradeManagementReportType(reportType)) {
    return EXIT_PLACEMENT_DETAIL_TAB_LABELS;
  }
  return ["Overview", "Market", "Simulator", "Pine", "Notes"] as const;
}

export function reportDetailTabTitles(reportType: string | undefined): readonly string[] {
  if (isTradeAnalysisReportType(reportType)) {
    return TRADE_ANALYSIS_DETAIL_TAB_TITLES;
  }
  if (isEntryAnalysisReportType(reportType)) {
    return ENTRY_ANALYSIS_DETAIL_TAB_TITLES;
  }
  if (isTradeManagementReportType(reportType)) {
    return EXIT_PLACEMENT_DETAIL_TAB_TITLES;
  }
  return ["Overview", "Entry Analysis", "Simulator", "PineScript", "Notes"] as const;
}
